import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getAllEmps,
  getMyEmps,
  createEmp,
  updateEmp,
  deleteEmp,
  searchEmps,
  clearErrors,
  clearMessage,
  resetSuccess,
} from "../../redux/empSlice";
import { Helmet } from "react-helmet-async";

const Emp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { emps, loading, error, success, message } = useSelector(
    (state) => state.emp
  );
  const { user } = useSelector((state) => state.user);

  const [formData, setFormData] = useState({
    name: "",
    number: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    number: "",
  });

  const [touched, setTouched] = useState({
    name: false,
    number: false,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showMyEmps, setShowMyEmps] = useState(false);

  // Validation functions
  const validateName = (name) => {
    if (!name || !name.trim()) {
      return "Employee name is required";
    }
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      return "Employee name must be at least 2 characters";
    }
    if (trimmed.length > 100) {
      return "Employee name cannot exceed 100 characters";
    }
    return "";
  };

  const validateNumber = (number) => {
    if (!number || !number.trim()) {
      return "Employee number is required";
    }
    const digitsOnly = number.replace(/\D/g, "");
    if (digitsOnly.length !== 10) {
      return "Employee number must be exactly 10 digits";
    }
    return "";
  };

  const validateField = (field, value) => {
    let error = "";
    switch (field) {
      case "name":
        error = validateName(value);
        break;
      case "number":
        error = validateNumber(value);
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
    return error === "";
  };

  const validateForm = () => {
    const nameValid = validateField("name", formData.name);
    const numberValid = validateField("number", formData.number);
    setTouched({ name: true, number: true });
    return nameValid && numberValid;
  };

  // Load employees on component mount
  useEffect(() => {
    if (showMyEmps && user) {
      dispatch(getMyEmps());
    } else {
      dispatch(getAllEmps());
    }
  }, [dispatch, showMyEmps, user]);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        dispatch(clearErrors());
        dispatch(clearMessage());
        dispatch(resetSuccess());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error, dispatch]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "number") {
      // Only allow digits, max 10
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: digitsOnly }));
      if (touched[name]) {
        validateField(name, digitsOnly);
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (touched[name]) {
        validateField(name, value);
      }
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const empData = {
      name: formData.name.trim(),
      number: formData.number.replace(/\D/g, ""),
    };

    try {
      if (isEditing) {
        await dispatch(updateEmp({ id: editingId, empData })).unwrap();
      } else {
        await dispatch(createEmp(empData)).unwrap();
      }
      
      resetForm();
      setShowModal(false);
      if (showMyEmps && user) {
        dispatch(getMyEmps());
      } else {
        dispatch(getAllEmps());
      }
    } catch (error) {
      // Error handled by Redux
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({ name: "", number: "" });
    setErrors({ name: "", number: "" });
    setTouched({ name: false, number: false });
    setIsEditing(false);
    setEditingId(null);
  };

  // Handle edit
  const handleEdit = (emp) => {
    setFormData({
      name: emp.name,
      number: emp.number,
    });
    setIsEditing(true);
    setEditingId(emp._id);
    setShowModal(true);
    setErrors({ name: "", number: "" });
    setTouched({ name: false, number: false });
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this employee?")) {
      try {
        await dispatch(deleteEmp(id)).unwrap();
        if (showMyEmps && user) {
          dispatch(getMyEmps());
        } else {
          dispatch(getAllEmps());
        }
      } catch (error) {
        // Error handled by Redux
      }
    }
  };

  // Handle search
  const handleSearch = useCallback(
    async (query) => {
      setSearchQuery(query);
      if (query.trim()) {
        dispatch(searchEmps(query));
      } else {
        if (showMyEmps && user) {
          dispatch(getMyEmps());
        } else {
          dispatch(getAllEmps());
        }
      }
    },
    [dispatch, showMyEmps, user]
  );

  // Toggle between all employees and my employees
  const handleToggleView = () => {
    setShowMyEmps(!showMyEmps);
    setSearchQuery("");
  };

  const showToast = useCallback((message, type = "success") => {
    const toast = document.createElement("div");
    toast.className = `emp-toast emp-toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add("emp-toast-show"), 100);
    setTimeout(() => {
      toast.classList.remove("emp-toast-show");
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  }, []);

  useEffect(() => {
    if (success && message) {
      showToast(message, "success");
    }
    if (error) {
      showToast(error, "error");
    }
  }, [success, error, message, showToast]);

  return (
    <>
      <Helmet>
        <title>Employee - RTS Books</title>
      </Helmet>
      <style>{`
        * {
          box-sizing: border-box;
        }

        @keyframes empFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes empSlideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes empShake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }

        .emp-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
          background-size: 400% 400%;
          animation: empFadeIn 0.6s ease;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .emp-wrapper {
          max-width: 1200px;
          margin: 0 auto;
        }

        .emp-header {
          text-align: center;
          margin-bottom: 30px;
          color: white;
        }

        .emp-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 10px;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
        }

        .emp-card {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          margin-bottom: 20px;
        }

        .emp-search-bar {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .emp-search-input {
          flex: 1;
          min-width: 200px;
          padding: 12px 16px;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .emp-search-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .emp-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .emp-btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
        }

        .emp-btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        }

        .emp-btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .emp-btn-secondary {
          background: #e1e5e9;
          color: #334155;
        }

        .emp-btn-secondary:hover {
          background: #cbd5e1;
        }

        .emp-btn-toggle {
          background: #10b981;
          color: white;
        }

        .emp-btn-toggle:hover {
          background: #059669;
        }

        .emp-btn-success {
          background: #10b981;
          color: white;
        }

        .emp-btn-success:hover {
          background: #059669;
          transform: translateY(-2px);
        }

        .emp-table-wrapper {
          overflow-x: auto;
          border-radius: 12px;
        }

        .emp-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 12px;
          overflow: hidden;
        }

        .emp-table thead {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .emp-table th {
          padding: 16px;
          text-align: left;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .emp-table td {
          padding: 16px;
          border-bottom: 1px solid #e1e5e9;
        }

        .emp-table tbody tr:hover {
          background: #f8f9fa;
        }

        .emp-table tbody tr:last-child td {
          border-bottom: none;
        }

        .emp-action-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-right: 8px;
        }

        .emp-btn-edit {
          background: #10b981;
          color: white;
        }

        .emp-btn-edit:hover {
          background: #059669;
          transform: translateY(-1px);
        }

        .emp-btn-delete {
          background: #ef4444;
          color: white;
        }

        .emp-btn-delete:hover {
          background: #dc2626;
          transform: translateY(-1px);
        }

        .emp-user-badge {
          display: inline-block;
          padding: 4px 8px;
          background: #e0e7ff;
          color: #4338ca;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .emp-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          backdrop-filter: blur(4px);
        }

        .emp-modal-content {
          background: white;
          border-radius: 20px;
          padding: 30px;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          animation: empSlideIn 0.3s ease;
        }

        .emp-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .emp-modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
        }

        .emp-modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #64748b;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .emp-modal-close:hover {
          background: #f1f5f9;
          color: #1e293b;
        }

        .emp-form-group {
          margin-bottom: 20px;
        }

        .emp-label {
          display: block;
          margin-bottom: 8px;
          color: #334155;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .emp-label.required::after {
          content: " *";
          color: #dc2626;
        }

        .emp-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .emp-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .emp-input.error {
          border-color: #dc2626;
          animation: empShake 0.5s ease;
        }

        .emp-error {
          color: #dc2626;
          font-size: 0.85rem;
          margin-top: 6px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .emp-modal-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }

        .emp-loading {
          text-align: center;
          padding: 40px;
          color: #64748b;
        }

        .emp-empty {
          text-align: center;
          padding: 40px;
          color: #64748b;
        }

        .emp-toast {
          position: fixed;
          top: 20px;
          right: -300px;
          background: #fff;
          color: #333;
          padding: 15px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          z-index: 10000;
          transition: all 0.3s ease;
          max-width: 350px;
          font-weight: 500;
        }

        .emp-toast.emp-toast-success {
          border-left: 4px solid #059669;
        }

        .emp-toast.emp-toast-error {
          border-left: 4px solid #dc2626;
        }

        .emp-toast.emp-toast-show {
          right: 20px;
          animation: empSlideIn 0.3s ease;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .emp-container {
            padding: 15px;
          }

          .emp-title {
            font-size: 2rem;
          }

          .emp-card {
            padding: 20px;
            border-radius: 16px;
          }

          .emp-search-bar {
            flex-direction: column;
          }

          .emp-search-input {
            min-width: 100%;
          }

          .emp-table {
            font-size: 0.9rem;
          }

          .emp-table th,
          .emp-table td {
            padding: 12px 8px;
          }

          .emp-action-btn {
            padding: 6px 12px;
            font-size: 0.85rem;
            margin-right: 4px;
            margin-bottom: 4px;
          }

          .emp-modal-content {
            padding: 20px;
            margin: 10px;
          }

          .emp-modal-title {
            font-size: 1.3rem;
          }
        }

        @media (max-width: 480px) {
          .emp-title {
            font-size: 1.5rem;
          }

          .emp-card {
            padding: 15px;
          }

          .emp-table {
            font-size: 0.85rem;
          }

          .emp-table th,
          .emp-table td {
            padding: 10px 6px;
          }

          .emp-table th:nth-child(3),
          .emp-table td:nth-child(3) {
            display: none;
          }

          .emp-action-btn {
            padding: 6px 10px;
            font-size: 0.8rem;
          }

          .emp-modal-content {
            padding: 15px;
          }

          .emp-btn {
            padding: 10px 16px;
            font-size: 0.9rem;
          }
        }

        @media (max-width: 360px) {
          .emp-table th,
          .emp-table td {
            padding: 8px 4px;
            font-size: 0.8rem;
          }

          .emp-action-btn {
            padding: 4px 8px;
            font-size: 0.75rem;
          }
        }
      `}</style>

      <div className="emp-container">
        <div className="emp-wrapper">
          <div className="emp-header">
            <h1 className="emp-title">Employee Management</h1>
          </div>

          <div className="emp-card">
            <div className="emp-search-bar">
              <input
                type="text"
                className="emp-search-input"
                placeholder="Search employees by name or number..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {user && (
                <button
                  className="emp-btn emp-btn-toggle"
                  onClick={handleToggleView}
                >
                  {showMyEmps ? "Show All" : "Show My Employees"}
                </button>
              )}
              <button
                className="emp-btn emp-btn-success"
                onClick={() => navigate("/emp-report")}
              >
                📊 View Report
              </button>
              <button
                className="emp-btn emp-btn-primary"
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
              >
                + Add Employee
              </button>
            </div>

            {loading && <div className="emp-loading">Loading...</div>}

            {!loading && emps.length === 0 && (
              <div className="emp-empty">No employees found</div>
            )}

            {!loading && emps.length > 0 && (
              <div className="emp-table-wrapper">
                <table className="emp-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Number</th>
                      <th>Created By</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emps.map((emp) => (
                      <tr key={emp._id}>
                        <td>{emp.name}</td>
                        <td>{emp.number}</td>
                        <td>
                          {emp.user ? (
                            <span className="emp-user-badge">
                              {emp.user.name || emp.user.phone || "Unknown"}
                            </span>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>
                          {new Date(emp.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          <button
                            className="emp-action-btn emp-btn-edit"
                            onClick={() => handleEdit(emp)}
                          >
                            Edit
                          </button>
                          <button
                            className="emp-action-btn emp-btn-delete"
                            onClick={() => handleDelete(emp._id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="emp-modal" onClick={() => setShowModal(false)}>
          <div
            className="emp-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="emp-modal-header">
              <h2 className="emp-modal-title">
                {isEditing ? "Edit Employee" : "Add New Employee"}
              </h2>
              <button
                className="emp-modal-close"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="emp-form-group">
                <label className="emp-label required" htmlFor="name">
                  Employee Name
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("name")}
                  placeholder="Enter employee name"
                  className={`emp-input ${
                    errors.name && touched.name ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.name && touched.name && (
                  <div className="emp-error">⚠️ {errors.name}</div>
                )}
              </div>

              <div className="emp-form-group">
                <label className="emp-label required" htmlFor="number">
                  Employee Number
                </label>
                <input
                  id="number"
                  type="tel"
                  name="number"
                  value={formData.number}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("number")}
                  placeholder="Enter 10-digit number"
                  maxLength={10}
                  className={`emp-input ${
                    errors.number && touched.number ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.number && touched.number && (
                  <div className="emp-error">⚠️ {errors.number}</div>
                )}
              </div>

              <div className="emp-modal-actions">
                <button
                  type="button"
                  className="emp-btn emp-btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="emp-btn emp-btn-primary"
                  disabled={loading}
                >
                  {loading ? "Saving..." : isEditing ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Emp;

